import React from 'react';
import {Card} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {CustomImageHandler} from '../../../../../../../shared/components/CustomImageHandler';
import {RatingStar} from '../../../../../../../shared/components/RatingStar';
import {calculateTotalReach, clearHtmlFromString, getCorrectSocialUrl,} from '../../../../../../../shared/functions/Functions';
import {numberFormatter} from '../../../../../../../utils/helpers/numberFormater';
import {useDispatch, useSelector} from 'react-redux';
import {IAllStates} from '../../../../../../../store/rootReducer';
import {useNavigate} from 'react-router-dom';
import {InternalRoutes} from '../../../../../../../enum/InternalRoutes';
import {priceSplitter} from '../../../../../../../utils/helpers/priceSplitter';
import {setActiveTab} from '../../../../../../../store/brand/contractBrandSlice';

function ContractModalProfileInfo() {
    const { campaign, user, initialContract } = useSelector((state: IAllStates) => state.contractBrand);
    const { isClient } = useSelector((state: IAllStates) => state.auth.user);
    const isProfileDisabled = campaign?.contracts?.[0]?.isProposed || isClient;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const goToContentTab = () => {
        dispatch(setActiveTab('1'));
    };
    const goToProfileInfo = () => {
        if (isProfileDisabled) {
            return;
        }
        navigate(`${InternalRoutes.INFLUENCER_PUBLIC_PROFILE}/${user?.id}`);
    };
    const engRate = Number(user?.engagementRate).toFixed(1);
    const agencyName = user?.agencyCompany?.displayName;
    const pitch = campaign?.contracts[0]?.description || user?.about;
    return (
        <>
            <Card className="d-none d-xl-flex">
                <Card.Body className="d-flex flex-column align-items-center">
                    {campaign?.contracts[0]?.isLongTerm && (
                        <div className="diagonal-text">
                            <span className="diagonal-text-sm">
                                {t(`brand.campaign.briefing.overview_card.long_term`)}
                            </span>
                        </div>
                    )}
                    {campaign?.isCharity && (
                        <p className="position-top-right status pending text-warning">{t('general.barter')}</p>
                    )}
                    <CustomImageHandler
                        onClick={goToProfileInfo}
                        thumbnailPath={user?.profilePhoto?.thumbnail}
                        photoPath={user?.profilePhoto?.thumbnail as string}
                        altTag="User Profile"
                        classes={`profile-image ${!isProfileDisabled && 'cursor-pointer'}`}
                        name={user?.displayName}
                    />
                    <h6
                        onClick={goToProfileInfo}
                        className={`mt-3 mb-0 text-center ${!isProfileDisabled && 'cursor-pointer textHoverStrong'}`}
                    >
                        {user?.displayName}
                    </h6>
                    <p
                        onClick={goToProfileInfo}
                        className={`${agencyName ? 'mb-1' : 'mb-2'} text-muted ${
                            !isProfileDisabled && 'cursor-pointer'
                        }`}
                    >
                        {user?.firstName} {user?.lastName}
                    </p>
                    {agencyName && (
                        <p className="mb-2 text-muted font-12 d-flex align-items-center">
                            <i className="fi fi-rr-building d-flex mr-1" />
                            {t('brand.campaign.book.contract.modal.agency')}:&nbsp;
                            <span className="text-dark font-weight-semi-bold">{agencyName}</span>
                        </p>
                    )}
                    {user?.totalRating ? <RatingStar rating={user?.totalRating} /> : ''}
                    {!!user?.userCategories?.length && (
                        <div className="w-100 d-flex flex-wrap justify-content-center mt-2" style={{ gap: 6 }}>
                            {user.userCategories.map(category => (
                                <p key={category.id} className="status mb-0">{`${t(
                                    `general.category.${category.campaignCategory.name}`
                                )}`}</p>
                            ))}
                        </div>
                    )}

                    <div className="w-100 d-flex border-top border-bottom mt-3 py-3 text-center">
                        <div className="flex-fill border-right">
                            <h6 className="text-primary mb-0">
                                {numberFormatter(
                                    calculateTotalReach(campaign, initialContract?.contractItems, true) ?? 0
                                )}
                            </h6>
                            <span className="text-muted font-12">
                                {t('brand.campaign.book.offer.modal.total_reach')}
                            </span>
                        </div>
                        <div className="flex-fill">
                            <h6 className="text-primary mb-0">
                                {user?.engagementRate !== 0 ? priceSplitter(+engRate) + ' %' : 'n.a.'}
                            </h6>
                            <span className="text-muted font-12">
                                {t('brand.campaign.book.offer.modal.engagement_rate')}
                            </span>
                        </div>
                    </div>

                    {pitch && (
                        <div className="w-100 mt-3">
                            <h6 className="mb-1">{t('brand.campaign.book.offer.modal.content_pitch')}</h6>
                            <div className="five-line-text text-muted">{clearHtmlFromString(pitch)}</div>
                            <a className="d-block text-end text-primary cursor-pointer mt-1" onClick={goToContentTab}>
                                {t('general.read_more')}
                            </a>
                        </div>
                    )}

                    {!!campaign?.userHandles?.length && (
                        <div className="w-100 border-top mt-3 pt-3">
                            <p className="text-muted mb-2">
                                {t('brand.campaign.book.offer.modal.check_social_networks', {
                                    userName: user?.firstName,
                                })}
                            </p>
                            <div className="d-flex flex-wrap">
                                {campaign.userHandles.map(item => (
                                    <a
                                        target="_blank"
                                        key={item.id}
                                        className="d-inline-flex align-items-center border rounded-pill px-2 py-1 mr-2 mb-2 text-dark"
                                        href={`${getCorrectSocialUrl(
                                            item?.mainPlatform?.platformEnum as string,
                                            item?.title,
                                            item?.externalLink
                                        )}`}
                                        rel="noreferrer"
                                    >
                                        <img src={item?.mainPlatform?.path} alt="Platform icon" height={20} />
                                        <span className="ml-1 font-12">@{item?.title}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
            <div
                onClick={goToProfileInfo}
                className={`d-xl-none d-flex align-items-center ${!isProfileDisabled && 'cursor-pointer'}`}
            >
                <div>
                    <CustomImageHandler
                        thumbnailPath={user?.profilePhoto?.thumbnail as string}
                        photoPath={user?.profilePhoto?.path as string}
                        classes="campaign-icon rounded-circle"
                        altTag="User Profile"
                        name={user?.displayName}
                    />
                </div>
                <h5 className="mb-0 ml-2">{user?.displayName}</h5>
            </div>
        </>
    );
}

export default ContractModalProfileInfo;
